import React, { useEffect, useMemo, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardActionArea,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  useDeleteUserRoleMutation,
  useGetUserInfoOnyQuery,
  useGetUserRoleQuery,
} from "../../../Api/attoDeskApi";
import { IUserAdmin, IUserRole } from "../../../Api/Interface/api.interface";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { CiSearch } from "react-icons/ci";
import { useStyles } from "../../../theme/appStyles";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import TuneIcon from "@mui/icons-material/Tune";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import AddIcon from "@mui/icons-material/Add";
import Pagination from "../../../Components/Pagination";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import {
  deriveDescription,
  deriveCategory,
  parsePrivilegeName,
} from "../Components/permissionUtils";
import { useNavigate } from "react-router-dom";

type SortKey = "name" | "permissions" | "users";

// Categories a role touches, derived from its permission names.
const deriveCategories = (allPrivileges: string[] = []): string[] => {
  const set = new Set<string>();
  allPrivileges.forEach((name) => {
    const { module } = parsePrivilegeName(name);
    set.add(deriveCategory(module));
  });
  return Array.from(set).sort();
};

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(8);
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IUserRole | null>(
    null
  );

  const { data, isLoading, isError, refetch } = useGetUserRoleQuery({
    searchText: currentSearchQuery,
  });

  const { data: userData, isLoading: userIsLoading } = useGetUserInfoOnyQuery({
    searchText: "",
  });

  const userList = useMemo(() => {
    return (userData?.data as IUserAdmin[]) || [];
  }, [userData?.data]);

  // created_by id -> "Role - username"
  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    userList.forEach((user) => {
      map.set(
        String(user.id),
        `${toProperCase(user.user_role_name)} - ${user.username}`
      );
    });
    return map;
  }, [userList]);

  // role id -> number of users assigned to that role
  const usersByRole = useMemo(() => {
    const map = new Map<string, number>();
    userList.forEach((u) => {
      const key = String(u.user_role);
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [userList]);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      setPage(0);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const goToView = (role: IUserRole) => navigate(`/user/role/${role.id}`);
  const goToEdit = (role: IUserRole) => navigate(`/user/role/${role.id}/edit`);
  const goToNew = () => navigate("/user/role/new");

  const handleOpenDeletePopup = (coursing: IUserRole) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };
  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const [deleteCategory] = useDeleteUserRoleMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage(response.message);
        setOpenDeleteCategory(false);
        refetch();
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to delete the user role");
    }
  };

  const coursings: IUserRole[] = Array.isArray(data?.data)
    ? (data?.data as IUserRole[])
    : [];

  const sorted = useMemo(() => {
    const arr = [...coursings];
    arr.sort((a, b) => {
      if (sortKey === "name") {
        return (a.roleName || "").localeCompare(b.roleName || "");
      }
      if (sortKey === "permissions") {
        return (b.allPrivileges?.length || 0) - (a.allPrivileges?.length || 0);
      }
      // users
      const aC = usersByRole.get(String(a.id)) || 0;
      const bC = usersByRole.get(String(b.id)) || 0;
      return bC - aC;
    });
    return arr;
  }, [coursings, sortKey, usersByRole]);

  const paged = sorted.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const goToUsersForRole = (roleId: string) => {
    // Users list page — links out with a role filter query param.
    navigate(`/user/list?role=${encodeURIComponent(roleId)}`);
  };

  return (
    <Box sx={{ background: "#f8fafc", minHeight: "100%", p: { xs: 1, md: 0 } }}>
      <Card
        className={classes.card}
        sx={{ background: "transparent", boxShadow: "none" }}
      >
        {/* Header */}
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ p: 2 }}
        >
          <Grid item>
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#0f172a" }}>
              User Roles
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: 14 }}>
              Manage staff access and permissions
            </Typography>
          </Grid>
          <Grid item>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search roles…"
                sx={{ background: "#fff", minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 170, background: "#fff" }}>
                <InputLabel id="role-sort-label">Sort by</InputLabel>
                <Select
                  labelId="role-sort-label"
                  label="Sort by"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as SortKey)}
                >
                  <MenuItem value="name">Name (A–Z)</MenuItem>
                  <MenuItem value="permissions">Most permissions</MenuItem>
                  <MenuItem value="users">Most users</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={goToNew}
              >
                Add Role
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {/* Role cards */}
        <Box sx={{ px: 2, pb: 2 }}>
          {isLoading || userIsLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="30vh"
            >
              <StepUpSpinner size="medium" />
            </Box>
          ) : isError || coursings.length === 0 ? (
            <Box sx={{ textAlign: "center", color: "#94a3b8", py: 6 }}>
              {isError ? "Error fetching data" : "No roles available"}
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {paged.map((row) => {
                const permCount = row.allPrivileges?.length || 0;
                const userCount = usersByRole.get(String(row.id)) || 0;
                const description = deriveDescription(row.allPrivileges);
                const categories = deriveCategories(row.allPrivileges);
                const creatorName = userMap.get(String(row.created_by));
                const creatorFallback =
                  !row.created_by || Number(row.created_by) === 0
                    ? "System"
                    : `User #${row.created_by}`;
                return (
                  <Card
                    key={row.id}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                      transition: "box-shadow .2s, transform .2s",
                      "&:hover": {
                        boxShadow: "0 6px 18px rgba(15,23,42,0.08)",
                      },
                    }}
                  >
                    <Grid container alignItems="stretch">
                      {/* Clickable primary area → view */}
                      <Grid item xs={12} md={9}>
                        <CardActionArea
                          onClick={() => goToView(row)}
                          sx={{ p: 2, height: "100%", borderRadius: 3 }}
                        >
                          <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12} md={5}>
                              <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: "#eef2ff",
                                    color: "#4f46e5",
                                    width: 44,
                                    height: 44,
                                  }}
                                >
                                  <AdminPanelSettingsOutlinedIcon />
                                </Avatar>
                                <Box>
                                  <Typography
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: 16,
                                      color: "#0f172a",
                                    }}
                                  >
                                    {toProperCase(row.roleName)}
                                  </Typography>
                                  <Typography
                                    sx={{ color: "#64748b", fontSize: 13 }}
                                  >
                                    {description}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} md={7}>
                              <Stack
                                direction="row"
                                spacing={0.75}
                                flexWrap="wrap"
                                rowGap={0.75}
                                alignItems="center"
                              >
                                {/* Users chip: clickable link when non-zero */}
                                {userCount > 0 ? (
                                  <Chip
                                    icon={<GroupsOutlinedIcon />}
                                    label={`${userCount} user${
                                      userCount === 1 ? "" : "s"
                                    }`}
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      goToUsersForRole(String(row.id));
                                    }}
                                    sx={{
                                      background: "#eef2ff",
                                      color: "#3730a3",
                                      cursor: "pointer",
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    icon={<GroupsOutlinedIcon />}
                                    label="No users assigned"
                                    size="small"
                                    sx={{
                                      background: "#f1f5f9",
                                      color: "#64748b",
                                    }}
                                  />
                                )}
                                <Chip
                                  icon={<KeyOutlinedIcon />}
                                  label={`${permCount} permissions`}
                                  size="small"
                                  sx={{
                                    background: "#ecfdf5",
                                    color: "#047857",
                                  }}
                                />
                                {categories.map((cat) => (
                                  <Chip
                                    key={cat}
                                    label={cat}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderColor: "#c7d2fe",
                                      color: "#4338ca",
                                      background: "#f5f3ff",
                                    }}
                                  />
                                ))}
                                <Tooltip
                                  title={
                                    creatorName
                                      ? `Created by ${creatorName}`
                                      : `Creator not available (${creatorFallback})`
                                  }
                                >
                                  <Chip
                                    label={`By ${creatorName || creatorFallback}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      maxWidth: 220,
                                      color: "#64748b",
                                      borderColor: "#e2e8f0",
                                    }}
                                  />
                                </Tooltip>
                              </Stack>
                            </Grid>
                          </Grid>
                        </CardActionArea>
                      </Grid>

                      {/* Actions column */}
                      <Grid
                        item
                        xs={12}
                        md={3}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: { xs: "flex-start", md: "flex-end" },
                          p: 2,
                        }}
                      >
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="View details">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                goToView(row);
                              }}
                              sx={{ border: "1px solid #e2e8f0" }}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit permissions">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                goToEdit(row);
                              }}
                              sx={{
                                border: "1px solid #c7d2fe",
                                color: "#4f46e5",
                              }}
                            >
                              <TuneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete role">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDeletePopup(row);
                              }}
                              sx={{
                                border: "1px solid #fecaca",
                                color: "#dc2626",
                              }}
                            >
                              <DeleteOutlineSharpIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Card>
                );
              })}
            </Stack>
          )}

          {/* Pagination */}
          {!isLoading && coursings.length > rowsPerPage && (
            <Grid container justifyContent="flex-end" sx={{ pt: 3 }}>
              <Pagination
                count={coursings.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
              />
            </Grid>
          )}
        </Box>
      </Card>

      {coursingToDelete && (
        <DeletePopup
          open={openDeleteCategory}
          handleCloseDelete={handleCloseDeletePopup}
          onConfirm={async () => {
            await handleDelete(coursingToDelete.id.toString());
          }}
          title="Delete User Role"
          content={(() => {
            const assigned =
              usersByRole.get(String(coursingToDelete.id)) || 0;
            if (assigned > 0) {
              return `"${coursingToDelete.roleName}" has ${assigned} user(s) assigned. Deleting it will remove their role — they will keep their account but have no access until reassigned to another role. Continue?`;
            }
            return `Are you sure you want to delete "${coursingToDelete.roleName}"?`;
          })()}
        />
      )}
    </Box>
  );
};

export default Index;
